import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Pagination from '../components/Pagination';
import { useCompanyHowToApply, useCreateCompanyHowToApply, useUpdateCompanyHowToApply, useDeleteCompanyHowToApply } from '../hooks/useCompanyHowToApply';

const CompanyHowToApply = ({ user, onLogout }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    'title[en]': '',
    'title[ar]': 'عنوان افتراضي', // Fixed Arabic title
    'subtitle[en]': '',
    'subtitle[ar]': 'عنوان فرعي افتراضي', // Fixed Arabic subtitle
    photo: null
  });

  // Fetch data using React Query
  const {
    data: companyHowToApplyData,
    isLoading,
    error,
    isFetching,
  } = useCompanyHowToApply(pagination.current, pagination.pageSize);

  // Mutations
  const createMutation = useCreateCompanyHowToApply();
  const updateMutation = useUpdateCompanyHowToApply();
  const deleteMutation = useDeleteCompanyHowToApply();

  // Memoized data and pagination
  const { data, totalRecords } = useMemo(() => {
    if (!companyHowToApplyData) {
      return { data: [], totalRecords: 0 };
    }

    return {
      data: companyHowToApplyData.data || [],
      totalRecords: companyHowToApplyData.total || 0,
    };
  }, [companyHowToApplyData]);

  // Handle pagination changes
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination({ current: 1, pageSize: newPageSize });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  // Handle textarea changes for subtitle
  const handleSubtitleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      'title[en]': '',
      'title[ar]': 'عنوان افتراضي',
      'subtitle[en]': '',
      'subtitle[ar]': 'عنوان فرعي افتراضي',
      photo: null
    });
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      'title[en]': item.title?.en || '',
      'title[ar]': item.title?.ar || 'عنوان افتراضي',
      'subtitle[en]': Array.isArray(item.subtitle?.en) ? item.subtitle.en.join('\n') : (item.subtitle?.en || ''),
      'subtitle[ar]': Array.isArray(item.subtitle?.ar) ? item.subtitle.ar.join('\n') : (item.subtitle?.ar || 'عنوان فرعي افتراضي'),
      photo: null // Reset photo field for edit
    });
    setShowEditModal(true);
  };

  // Handle add submit
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title[en]', formData['title[en]']);
      formDataToSend.append('title[ar]', formData['title[ar]']);
      formDataToSend.append('subtitle[en]', formData['subtitle[en]']);
      formDataToSend.append('subtitle[ar]', formData['subtitle[ar]']);
      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      await createMutation.mutateAsync(formDataToSend);
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating item:', error);
    }
  };

  // Handle edit submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title[en]', formData['title[en]']);
      formDataToSend.append('title[ar]', formData['title[ar]']);
      formDataToSend.append('subtitle[en]', formData['subtitle[en]']);
      formDataToSend.append('subtitle[ar]', formData['subtitle[ar]']);
      formDataToSend.append('_method', 'PUT');
      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      await updateMutation.mutateAsync({
        id: selectedItem.id,
        data: formDataToSend
      });
      setShowEditModal(false);
      setSelectedItem(null);
      resetForm();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${item.title?.en || 'this item'}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#333',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(item.id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Item has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      } catch (error) {
        console.error('Error deleting item:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete item',
          confirmButtonText: 'OK',
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      }
    }
  };

  // Check if any mutation is loading
  const isMutating = createMutation.isPending || 
                    updateMutation.isPending || 
                    deleteMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      createMutation.error?.message || 
                      updateMutation.error?.message || 
                      deleteMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'photo',
      title: 'Photo',
      render: (value, row) => (
        <div className="flex-shrink-0 h-16 w-16">
          <img 
            className="h-16 w-16 rounded-lg object-cover" 
            src={value} 
            alt={row.title?.en || 'Photo'} 
          />
        </div>
      ),
      sortable: false
    },
    {
      key: 'title',
      title: 'Title',
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          {value?.en || 'N/A'}
        </div>
      )
    },
    {
      key: 'subtitle',
      title: 'Subtitle',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value?.en ? (
            <ul className="list-disc list-inside">
              {Array.isArray(value.en) ? value.en.map((sub, index) => (
                <li key={index}>{sub}</li>
              )) : (
                <li>{value.en}</li>
              )}
            </ul>
          ) : (
            'N/A'
          )}
        </div>
      ),
      sortable: false
    }
  ];

  // Table actions configuration
  const actions = [
    {
      key: 'edit',
      title: 'Edit',
      icon: <FiEdit className="h-4 w-4" />,
      variant: 'primary'
    },
    {
      key: 'delete',
      title: 'Delete',
      icon: <FiTrash2 className="h-4 w-4" />,
      variant: 'danger'
    }
  ];

  // Handle row actions
  const handleRowAction = (actionKey, row) => {
    switch (actionKey) {
      case 'edit':
        handleEdit(row);
        break;
      case 'delete':
        handleDelete(row);
        break;
      default:
        break;
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="Company - How to Apply"
          subtitle="Start Hiring Students in 3 Easy Steps"
          user={user}
        />

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800 text-sm">{errorMessage}</div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          {/* Header with add button */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {isFetching && !isLoading && (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    <span className="text-sm text-gray-600">Updating...</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleAdd}
                disabled={isMutating}
                className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                <FiPlus className="h-4 w-4" />
                <span>Add New</span>
              </button>
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600">Loading data...</span>
              </div>
            ) : (
              <DataTable
                data={data}
                columns={columns}
                loading={isLoading}
                searchable={false}
                filterable={false}
                exportable={true}
                actions={actions}
                onRowAction={handleRowAction}
                emptyMessage="No data available"
              />
            )}
          </div>

          {/* Pagination */}
          {!isLoading && data.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <Pagination
                current={pagination.current}
                total={totalRecords}
                pageSize={pagination.pageSize}
                onChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                showSizeChanger={true}
                showTotal={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Company Item</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title (English) *
                </label>
                <input
                  type="text"
                  name="title[en]"
                  value={formData["title[en]"]}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle (English) *
                </label>
                <textarea
                  name="subtitle[en]"
                  value={formData["subtitle[en]"]}
                  onChange={handleSubtitleChange}
                  required
                  rows={4}
                  placeholder="Enter each subtitle item on a new line"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter each subtitle item on a new line
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photo *
                </label>
                <input
                  type="file"
                  name="photo"
                  onChange={handleInputChange}
                  accept="image/*"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  disabled={createMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                >
                  {createMutation.isPending ? "Adding..." : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Company Item</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title (English) *
                </label>
                <input
                  type="text"
                  name="title[en]"
                  value={formData["title[en]"]}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle (English) *
                </label>
                <textarea
                  name="subtitle[en]"
                  value={formData["subtitle[en]"]}
                  onChange={handleSubtitleChange}
                  required
                  rows={4}
                  placeholder="Enter each subtitle item on a new line"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter each subtitle item on a new line
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photo
                </label>
                <input
                  type="file"
                  name="photo"
                  onChange={handleInputChange}
                  accept="image/*"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to keep current photo
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                >
                  {updateMutation.isPending ? "Updating..." : "Update Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CompanyHowToApply; 